import React, { useMemo } from 'react';
import {
    Logout as LogoutIcon,
    ChevronLeft,
    Menu as MenuIcon,
    ChevronRight,
    Mail,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
import i18next from 'i18next';

import { useTheme } from '@mui/material/styles';
import { Avatar, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import DarkModeToggle from './../global/DarkModeToggle';
import { Drawer, DrawerHeader } from './UI';
import { sidebarItems } from '../../constants/sidebarItems';

import { useDarkMode } from '../../context/DarkModeContext';
import { useAuthContext } from '../../auth/useAuthContext';
import { usePermissions } from '../../auth/PermissionsContext';

const SideBar = ({ open, setOpen }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const navigate = useNavigate();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();

    const lng = i18next.language || 'en';
    const isArabic = lng === 'ar';

    const { toggleDarkMode } = useDarkMode();
    const { logout, user } = useAuthContext();
    const { hasPrefix, isInitialized, isAuthenticated } = usePermissions();

    const normalizePhotoUrl = (url) => {
        if (!url) return '';
        if (typeof url !== 'string') return url;
        try {
            const u = new URL(url);
            u.pathname = u.pathname.replace(/^\/api\//, '/');
            return u.toString();
        } catch (e) {
            return url.replace(/^\/?api\//, '/');
        }
    };

    const permissionPrefixByPath = useMemo(
        () => ({
            '/dashboard': null,
            '/schools': 'schools_',
            '/academic-years': 'academic_years_',
            '/academic-terms': 'academic_terms_',
            '/levels': 'levels_',
            '/departments': 'departments_',
            '/job-titles': 'job_titles_',
            '/org-structure-items': 'org_structure_items_',
            '/employees': 'employees_',
            '/teachers': 'teachers_',
            '/classes': 'classes_',
            '/guardians': 'guardians_',
            '/students': 'students_',
            '/student-enrollments': 'student_enrollments_',
            '/org-structure-assignments': 'org_structure_assignments_',
            '/users': 'users_',
            '/roles': 'roles_',
        }),
        []
    );

    const filteredItems = useMemo(() => {
        const canSeeItem = (item) => {
            const prefix = permissionPrefixByPath[item.path];
            if (!prefix) return true;
            return hasPrefix(prefix);
        };
        return sidebarItems.filter(canSeeItem);
    }, [permissionPrefixByPath, hasPrefix]);

    const handleToggleDrawer = () => setOpen((prev) => !prev);

    const handleLogout = async () => {
        try {
            const response = await logout();
            const message = response?.data?.message || (isArabic ? 'تم تسجيل الخروج' : 'Logged out successfully');
            enqueueSnackbar(message, { variant: 'success' });
            navigate('/login');
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message || error?.message || (isArabic ? 'فشل تسجيل الخروج' : 'Logout failed');
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    };

    const blockMenuUntilReady = !isInitialized || !isAuthenticated;

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            <Drawer
                variant="permanent"
                open={open}
                anchor={isArabic ? 'right' : 'left'}
                sx={{
                    '& .MuiDrawer-paper': {
                        maxWidth: 240,
                        overflowX: 'hidden',
                        bgcolor: isDark ? '#020617' : '#0D1B2A',
                        backgroundImage: isDark
                            ? 'radial-gradient(circle at 0 0, rgba(56,189,248,0.35), transparent 55%), radial-gradient(circle at 0 0, rgba(59,140,246,0.45), transparent 55%)'
                            : undefined,
                        color: '#E5E7EB',
                        borderRight: !isArabic ? `1px solid ${theme.palette.divider}` : 'none',
                        borderLeft: isArabic ? `1px solid ${theme.palette.divider}` : 'none',
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(148,163,184,0.8) transparent',
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(148,163,184,0.6)',
                            borderRadius: 999,
                        },
                        '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(148,163,184,0.9)' },
                    },
                }}
            >
                <DrawerHeader>
                    <IconButton onClick={handleToggleDrawer} sx={{ color: '#E5E7EB' }}>
                        {open ? (isArabic ? <ChevronRight /> : <ChevronLeft />) : <MenuIcon />}
                    </IconButton>
                </DrawerHeader>

                <Divider sx={{ borderColor: 'rgba(148,163,184,0.35)' }} />
                <Box
                    sx={{
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: open ? 'flex-start' : 'center',
                        mt: 1.5,
                        mb: 1,
                        px: open ? 1 : 0,
                        cursor: 'pointer',
                    }}
                    onClick={() => navigate('/profile')}
                >
                    <Avatar
                        src={normalizePhotoUrl(user?.image?.thumb || user?.image)}
                        sx={{
                            width: 32,
                            height: 32,
                            boxShadow: '0 0 0 2px rgba(15,23,42,0.5)',
                        }}
                    />

                    {open && (
                        <Box sx={{ ml: isArabic ? 0 : 1.5, mr: isArabic ? 1.5 : 0 }}>
                            <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: '#F9FAFB' }}>
                                <bdi>{user?.name ? `Hi ${user?.name}` : 'Hi'}</bdi>
                            </Typography>

                            <Typography noWrap sx={{ fontSize: 12, color: '#9CA3AF' }}>
                                {user?.email}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Divider sx={{ borderColor: 'rgba(148,163,184,0.3)', mb: 1 }} />

                {open && user?.email && (
                    <ListItem disablePadding sx={{ display: 'block' }}>
                        <ListItemButton
                            sx={{
                                minHeight: 42,
                                justifyContent: open ? 'initial' : 'center',
                                px: 1.5,
                                mx: open ? 0.5 : 0,
                                color: '#E5E7EB',
                                '&:hover': { bgcolor: 'rgba(148,163,184,0.18)' },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    ml: isArabic ? (open ? 2 : 0) : 0,
                                    mr: !isArabic ? (open ? 2 : 0) : 0,
                                    justifyContent: 'center',
                                    color: '#93C5FD',
                                }}
                            >
                                <Mail fontSize="small" />
                            </ListItemIcon>

                            <ListItemText
                                primary={user.email}
                                sx={{
                                    display: open ? 'block' : 'none',
                                    textAlign: isArabic ? 'right' : 'left',
                                    '& .MuiTypography-root': { fontSize: 12 },
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                )}

                <List>
                    {!blockMenuUntilReady &&
                        filteredItems.map((item) => {
                            const Icon = item.icon;
                            const active = location.pathname.startsWith(item.path);

                            return (
                                <ListItem key={item.key} disablePadding sx={{ display: 'block' }}>
                                    <ListItemButton
                                        onClick={() => navigate(item.path)}
                                        sx={{
                                            minHeight: 44,
                                            justifyContent: open ? 'initial' : 'center',
                                            px: 1.5,
                                            mx: open ? 1 : 0,
                                            my: 0.25,
                                            color: '#E5E7EB',
                                            borderRadius: 1.5,
                                            bgcolor: active ? 'rgba(59,130,246,0.22)' : 'transparent',
                                            '&:hover': { bgcolor: 'rgba(59,130,246,0.18)' },
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                ml: isArabic ? (open ? 2 : 0) : 0,
                                                mr: !isArabic ? (open ? 2 : 0) : 0,
                                                justifyContent: 'center',
                                                color: active ? '#93C5FD' : '#BFDBFE',
                                            }}
                                        >
                                            <Icon fontSize="small" />
                                        </ListItemIcon>

                                        <ListItemText
                                            primary={item.label?.[lng] || item.label?.en}
                                            sx={{
                                                display: open ? 'block' : 'none',
                                                textAlign: isArabic ? 'right' : 'left',
                                                '& .MuiTypography-root': { fontSize: 14 },
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                </List>

                <Divider sx={{ borderColor: 'rgba(148,163,184,0.3)', mt: 1, mb: 0.5 }} />

                <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                        onClick={handleLogout}
                        sx={{
                            minHeight: 48,
                            justifyContent: open ? 'initial' : 'center',
                            px: 1.5,
                            mx: open ? 0.5 : 0,
                            color: '#FCA5A5',
                            '&:hover': { bgcolor: 'rgba(248,114,114,0.16)' },
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                ml: isArabic ? (open ? 2 : 0) : 0,
                                mr: !isArabic ? (open ? 2 : 0) : 0,
                                justifyContent: 'center',
                                color: '#FCA5A5',
                            }}
                        >
                            <LogoutIcon fontSize="small" />
                        </ListItemIcon>

                        <ListItemText
                            primary={isArabic ? 'تسجيل الخروج' : 'Log Out'}
                            sx={{ display: open ? 'block' : 'none', '& .MuiTypography-root': { fontSize: 14 } }}
                        />
                    </ListItemButton>
                </ListItem>

                <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                        onClick={toggleDarkMode}
                        sx={{
                            minHeight: 48,
                            justifyContent: open ? 'initial' : 'center',
                            px: 1.5,
                            mx: open ? 0.5 : 0,
                            color: '#E5E7EB',
                            '&:hover': { bgcolor: 'rgba(148,163,184,0.18)' },
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                ml: isArabic ? (open ? 2 : 0) : 0,
                                mr: !isArabic ? (open ? 2 : 0) : 0,
                                justifyContent: 'center',
                                color: '#FACC15',
                            }}
                        >
                            <DarkModeToggle />
                        </ListItemIcon>

                        <ListItemText
                            primary={isArabic ? 'الوضع الليلي' : 'Theme'}
                            sx={{ display: open ? 'block' : 'none', '& .MuiTypography-root': { fontSize: 14 } }}
                        />
                    </ListItemButton>
                </ListItem>
            </Drawer>
        </Box>
    );
};

export default SideBar;
